/**
 * Sentry initialization — call initSentry() once before rendering the app.
 *
 * Loads /sentry-config.json a single time at startup, then calls Sentry.init.
 * SPA navigations do not re-fetch this file. Route tracing comes from
 * Sentry.wrapCreateBrowserRouter wrapping createBrowserRouter in routes/index.tsx,
 * not from a hooks-based integration, since this app uses a data router.
 *
 * Enabled only in UAT and prod when a DSN is present.
 */
import * as Sentry from '@sentry/react';
import { ENV } from '@config/env';

const SENTRY_ENABLED_ENVS = ['uat', 'production'];

const DEFAULT_TRACES_SAMPLE_RATE = 1.0;
const DEFAULT_REPLAYS_SESSION_SAMPLE_RATE = 0.1;
const DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE = 1.0;

// App rendering waits on this fetch, so it must always settle
const CONFIG_FETCH_TIMEOUT_MS = 3000;

interface SentryRuntimeConfig {
  dsn?: string;
  environment?: string;
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
}

function buildTimeConfig(): SentryRuntimeConfig {
  return {
    dsn: ENV.SENTRY_DSN,
    environment: ENV.SENTRY_ENVIRONMENT || ENV.APP_ENV,
    tracesSampleRate: DEFAULT_TRACES_SAMPLE_RATE,
    replaysSessionSampleRate: DEFAULT_REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE,
  };
}

function fetchSentryConfig(): Promise<SentryRuntimeConfig> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), CONFIG_FETCH_TIMEOUT_MS);

  return fetch('/sentry-config.json', { signal: controller.signal })
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json() as Promise<SentryRuntimeConfig>;
    })
    .catch(() => buildTimeConfig())
    .finally(() => clearTimeout(timeout));
}

export function initSentry(): Promise<void> {
  return fetchSentryConfig().then(config => {
    const dsn = config.dsn || ENV.SENTRY_DSN;
    const environment = config.environment || ENV.SENTRY_ENVIRONMENT || ENV.APP_ENV;

    Sentry.init({
      dsn,
      environment,
      enabled: SENTRY_ENABLED_ENVS.includes(environment) && !!dsn,
      sendDefaultPii: false,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],
      tracesSampleRate:
        typeof config.tracesSampleRate === 'number' ? config.tracesSampleRate : DEFAULT_TRACES_SAMPLE_RATE,
      replaysSessionSampleRate:
        typeof config.replaysSessionSampleRate === 'number'
          ? config.replaysSessionSampleRate
          : DEFAULT_REPLAYS_SESSION_SAMPLE_RATE,
      replaysOnErrorSampleRate:
        typeof config.replaysOnErrorSampleRate === 'number'
          ? config.replaysOnErrorSampleRate
          : DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE,
      tracePropagationTargets: [ENV.API_BASE_URL],
    });
  });
}
